
import { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export interface CashbackRulesFormData {
  percentage: string;
  multiplier: string;
  expiration_days: string;
  include_shipping: boolean;
}

export const useCashbackRules = () => {
  const [formData, setFormData] = useState<CashbackRulesFormData>({
    percentage: '',
    multiplier: '3',
    expiration_days: '30',
    include_shipping: true,
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalData, setOriginalData] = useState<CashbackRulesFormData | null>(null);
  const { toast } = useToast();

  const fetchRules = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('cashback_rules')
        .select('*')
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        const fetchedData = {
          percentage: data.percentage.toString(),
          multiplier: data.multiplier.toString(),
          expiration_days: data.expiration_days.toString(),
          include_shipping: data.include_shipping,
        };
        setFormData(fetchedData);
        setOriginalData(fetchedData);
      } else {
        // Se não existir regra ativa, criar uma padrão
        const defaultData = {
          percentage: '5.00',
          multiplier: '3',
          expiration_days: '30',
          include_shipping: true,
        };
        setFormData(defaultData);
        setOriginalData(defaultData);
      }
    } catch (error: any) {
      console.error('Erro ao carregar regras:', error);
      toast({
        title: "Erro ao carregar regras",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const createOrUpdateDefaultTemplate = async (rulesData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      // Verificar se já existe um template padrão
      const { data: existingTemplate } = await supabase
        .from('cashback_templates')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_default', true)
        .single();

      const templateData = {
        name: 'Cashback Padrão',
        description: 'Template padrão gerado automaticamente pelas regras de cashback',
        percentage: parseFloat(rulesData.percentage),
        multiplier: parseInt(rulesData.multiplier),
        expiration_days: parseInt(rulesData.expiration_days),
        discount_type: 'calculated' as const,
        is_active: true,
        is_default: true,
        user_id: user.id,
      };

      if (existingTemplate) {
        // Atualizar template existente
        const { error } = await supabase
          .from('cashback_templates')
          .update(templateData)
          .eq('id', existingTemplate.id);

        if (error) throw error;
      } else {
        // Criar novo template padrão
        const { error } = await supabase
          .from('cashback_templates')
          .insert(templateData);

        if (error) throw error;
      }
    } catch (error: any) {
      console.error('Erro ao criar/atualizar template padrão:', error);
      throw error;
    }
  };

  const saveRule = async () => {
    try {
      setIsSaving(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const rulesPayload = {
        user_id: user.id,
        percentage: parseFloat(formData.percentage),
        multiplier: parseInt(formData.multiplier),
        expiration_days: parseInt(formData.expiration_days),
        include_shipping: formData.include_shipping,
        is_active: true,
      };

      // Primeiro, desativar todas as regras existentes do usuário
      await supabase
        .from('cashback_rules')
        .update({ is_active: false })
        .eq('user_id', user.id);

      // Criar nova regra ativa
      const { error: rulesError } = await supabase
        .from('cashback_rules')
        .insert(rulesPayload);

      if (rulesError) throw rulesError;

      // Criar ou atualizar o template padrão
      await createOrUpdateDefaultTemplate(formData);

      setOriginalData({ ...formData });
      setHasUnsavedChanges(false);

      toast({
        title: "Configurações salvas",
        description: "Suas regras de cashback foram salvas e o template padrão foi atualizado.",
      });
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      toast({
        title: "Erro ao salvar",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const updateFormField = (field: keyof CashbackRulesFormData, value: any) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    // Verificar se houve alterações
    if (originalData) {
      const hasChanges = Object.keys(newFormData).some(key => {
        return newFormData[key as keyof CashbackRulesFormData] !== originalData[key as keyof CashbackRulesFormData];
      });
      setHasUnsavedChanges(hasChanges);
    }
  };

  const resetForm = () => {
    if (originalData) {
      setFormData({ ...originalData });
      setHasUnsavedChanges(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  return {
    formData,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    saveRule,
    updateFormField,
    resetForm,
  };
};
